import styled, { keyframes } from 'styled-components';
import { THUMBNAIL_HEIGHT } from '../consts';
import { ThumbnailDisplayInfo } from '../types';
import { getThumbnailFrameBackground } from '../utils';
import { ThumbnailFrame } from './ThumbnailFrame';
import { ThumbnailSprite } from './ThumbnailSprite';

export type ThumbnailProps = ThumbnailDisplayInfo;

const styledAnimation = (imageWidth: number) => keyframes`
  from {
      background-position: 0 0;
    }
    to {
      background-position: -${imageWidth}px 0;
    }
`;

type ThumbnailElProps = {
  $columns: number;
  $imageWidth: number;
};

const ThumbnailEl = styled.div<ThumbnailElProps>`
  animation: ${(p: ThumbnailElProps) => styledAnimation(p.$imageWidth)} 10s
    steps(${(p: ThumbnailElProps) => p.$columns}) infinite;
`;

/**
 * Logger filmstrip thumbnail: bordered frame around a sprite cell.
 * Optional CSS animation cycles columns horizontally (legacy filmstrip preview).
 */
export function Thumbnail({
  url,
  offset,
  metadata,
  animated = false,
  targetHeight = THUMBNAIL_HEIGHT,
}: ThumbnailProps) {
  if (!animated) {
    return (
      <ThumbnailFrame>
        <ThumbnailSprite
          url={url}
          metadata={metadata}
          frameIndex={offset}
          mode="px"
          targetHeight={targetHeight}
        />
      </ThumbnailFrame>
    );
  }

  // Animated path keeps CSS steps() for logger convenience; uses px background math.
  const frameStyles = getThumbnailFrameBackground(metadata, 0, 'px', targetHeight);
  const spriteWidthPx = Number.parseFloat(frameStyles.backgroundSize);

  return (
    <ThumbnailFrame>
      <ThumbnailEl
        $columns={metadata.columns}
        $imageWidth={spriteWidthPx}
        style={{
          backgroundImage: `url(${url})`,
          width: frameStyles.width,
          height: frameStyles.height,
          backgroundSize: frameStyles.backgroundSize,
          backgroundPosition: frameStyles.backgroundPosition,
          backgroundRepeat: frameStyles.backgroundRepeat,
        }}
      />
    </ThumbnailFrame>
  );
}
